// Intl formatting per Interface Language, one implementation for the SPA and
// the server. Locale tags: de -> de-DE, en -> en-GB (the plan's call), so
// USD reads "31,91 $" in German and "$31.91" in English.
import type { i18n } from "i18next";
import { DEFAULT_LOCALE, type Locale } from "../contract/auth";

export const LOCALE_TAGS: Readonly<Record<Locale, string>> = Object.freeze({
  de: "de-DE",
  en: "en-GB",
});

/** BCP 47 tag for Intl from an interface locale or an i18next language string; unknown falls back to German. */
export function localeTag(locale: string | undefined): string {
  const base = (locale ?? DEFAULT_LOCALE).toLowerCase().split("-")[0] as Locale;
  return LOCALE_TAGS[base] ?? LOCALE_TAGS[DEFAULT_LOCALE];
}

export interface DateFormatOptions {
  /** IANA zone; defaults to the runtime's zone. Tests pass one so output does not depend on the machine. */
  readonly timeZone?: string;
}
/**
 * The one contract for a value a screen has not got yet: a missing, null, or
 * unusable input reads as nothing at all. A Chapter without Chapter Audio has
 * no durationMs and an Audiobook that never rendered has no renderedAt, and
 * these functions are called straight from the render tree, so a thrown
 * RangeError would take the tree down and `new Date(null)` would print a
 * confident "01.01.1970".
 */
export const NOT_AVAILABLE = "";

/** The value as a finite number, or undefined for null, undefined, NaN, Infinity, and anything not a number. */
function finite(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

/** The value as a valid Date, or undefined for null, undefined, NaN, and an unparseable string. */
function validDate(value: Date | string | number | null | undefined): Date | undefined {
  if (value === null || value === undefined) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

const cache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat | Intl.DurationFormat>();
function cached<T>(key: string, make: () => T): T {
  const hit = cache.get(key);
  if (hit) return hit as T;
  const made = make();
  cache.set(key, made as Intl.NumberFormat);
  return made;
}

/** USD from cents (estimates.money_cents), narrow symbol: "31,91 $" / "$31.91". */
export function formatMoneyCents(
  cents: number | null | undefined,
  locale: string | undefined,
): string {
  const amount = finite(cents);
  if (amount === undefined) return NOT_AVAILABLE;
  const tag = localeTag(locale);
  return cached(
    `money:${tag}`,
    () =>
      new Intl.NumberFormat(tag, {
        style: "currency",
        currency: "USD",
        currencyDisplay: "narrowSymbol",
      }),
  ).format(amount / 100);
}

/** Grouped integer, e.g. character counts: "4.120" / "4,120". */
export function formatInteger(
  value: number | null | undefined,
  locale: string | undefined,
): string {
  const number = finite(value);
  if (number === undefined) return NOT_AVAILABLE;
  const tag = localeTag(locale);
  return cached(
    `integer:${tag}`,
    () => new Intl.NumberFormat(tag, { maximumFractionDigits: 0 }),
  ).format(number);
}

/**
 * One fraction digit for a small measured quantity, e.g. the seconds of silence
 * a Pause cue inserts: "0,4" in German, "0.4" in English. Every number the
 * interface shows goes through Intl, so a raw `{{seconds}}` interpolation of a
 * JavaScript number would print an English decimal point in the German
 * interface.
 */
export function formatDecimal(
  value: number | null | undefined,
  locale: string | undefined,
): string {
  const number = finite(value);
  if (number === undefined) return NOT_AVAILABLE;
  const tag = localeTag(locale);
  return cached(
    `decimal:${tag}`,
    () => new Intl.NumberFormat(tag, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
  ).format(number);
}

/** Calendar date: "05.09.2026" / "5 Sep 2026". */
export function formatDate(
  value: Date | string | number | null | undefined,
  locale: string | undefined,
  options: DateFormatOptions = {},
): string {
  const date = validDate(value);
  if (!date) return NOT_AVAILABLE;
  const tag = localeTag(locale);
  return cached(
    `date:${tag}:${options.timeZone ?? ""}`,
    () =>
      new Intl.DateTimeFormat(tag, {
        dateStyle: "medium",
        ...(options.timeZone ? { timeZone: options.timeZone } : {}),
      }),
  ).format(date);
}

/** Date and clock time for progress and download rows: "05.09.2026, 12:40" / "5 Sep 2026 at 12:40". */
export function formatDateTime(
  value: Date | string | number | null | undefined,
  locale: string | undefined,
  options: DateFormatOptions = {},
): string {
  const date = validDate(value);
  if (!date) return NOT_AVAILABLE;
  const tag = localeTag(locale);
  return cached(
    `dateTime:${tag}:${options.timeZone ?? ""}`,
    () =>
      new Intl.DateTimeFormat(tag, {
        dateStyle: "medium",
        timeStyle: "short",
        ...(options.timeZone ? { timeZone: options.timeZone } : {}),
      }),
  ).format(date);
}

/**
 * Duration in words for totals and estimates: hours and minutes from one
 * minute up, seconds below that. The whole length is rounded to the nearest
 * minute once, so 1 h 59 min 59 s reads "2 Std." rather than "1 Std., 59 Min.".
 * "2 Std., 41 Min." / "2 hrs, 41 min"; "40 Min." / "40 min".
 */
export function formatDuration(ms: number | null | undefined, locale: string | undefined): string {
  const value = finite(ms);
  if (value === undefined) return NOT_AVAILABLE;
  const tag = localeTag(locale);
  const milliseconds = Math.max(0, value);
  const totalSeconds = Math.round(milliseconds / 1000);
  if (totalSeconds < 60) {
    // Zero seconds must still read "0 Sek.", which the default display would hide.
    return cached(
      `duration-seconds:${tag}`,
      () => new Intl.DurationFormat(tag, { style: "short", secondsDisplay: "always" }),
    ).format({ seconds: totalSeconds });
  }
  // Rounded from the milliseconds, never from the rounded seconds: rounding
  // twice pushes 1:00:29.5 over the minute boundary and reads "1 hr, 1 min".
  const totalMinutes = Math.round(milliseconds / 60_000);
  return cached(`duration:${tag}`, () => new Intl.DurationFormat(tag, { style: "short" })).format({
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
  });
}

/**
 * A fraction 0..1 as a percentage: "64 %" in German, "64%" in English. The
 * Interface Language decides the space before the sign, which is why this is
 * not a string built from formatInteger and a literal "%".
 */
export function formatPercent(
  fraction: number | null | undefined,
  locale: string | undefined,
): string {
  const value = finite(fraction);
  if (value === undefined) return NOT_AVAILABLE;
  const tag = localeTag(locale);
  return cached(
    `percent:${tag}`,
    () => new Intl.NumberFormat(tag, { style: "percent", maximumFractionDigits: 0 }),
  ).format(value);
}

/**
 * Decimal byte units the way the mockups write a Source size, "148 MB" and
 * "1,5 MB": one decimal below ten so a small file does not collapse to "1 MB",
 * none above, and plain bytes under a kilobyte. Decimal, not binary, because
 * the number next to it is what an operating system's file listing shows.
 */
const BYTE_UNITS = ["byte", "kilobyte", "megabyte", "gigabyte", "terabyte"] as const;

/** `value` rounded to the number of decimals it is about to be printed with. */
function roundTo(value: number, digits: number): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

export function formatBytes(bytes: number | null | undefined, locale: string | undefined): string {
  const input = finite(bytes);
  if (input === undefined) return NOT_AVAILABLE;
  let value = Math.max(0, input);
  let index = 0;
  while (value >= 1000 && index < BYTE_UNITS.length - 1) {
    value /= 1000;
    index += 1;
  }
  // Round before the unit is final, because rounding can cross both boundaries
  // the rule above is written on. 999,999 bytes scales to 999.999 kB and no
  // decimal makes that 1000 kB, which has to step up and read "1,0 MB", not
  // "1.000 kB"; 9,960,000 bytes scales to 9.96 MB and one decimal makes that
  // 10.0, which is at ten and so carries no decimal at all.
  let digits = index > 0 && value < 10 ? 1 : 0;
  let shown = roundTo(value, digits);
  if (shown >= 1000 && index < BYTE_UNITS.length - 1) {
    index += 1;
    shown /= 1000;
    digits = shown < 10 ? 1 : 0;
    shown = roundTo(shown, digits);
  }
  if (digits === 1 && shown >= 10) {
    digits = 0;
    shown = roundTo(shown, 0);
  }
  const tag = localeTag(locale);
  return cached(
    `bytes:${tag}:${index}:${digits}`,
    () =>
      new Intl.NumberFormat(tag, {
        style: "unit",
        unit: BYTE_UNITS[index],
        unitDisplay: "short",
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }),
  ).format(shown);
}

/** Clock form for a chapter's length, locale-free: "4:12", "1:02:03". */
export function formatClock(ms: number | null | undefined): string {
  const value = finite(ms);
  if (value === undefined) return NOT_AVAILABLE;
  const totalSeconds = Math.max(0, Math.round(value / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const two = (n: number) => String(n).padStart(2, "0");
  return hours > 0 ? `${hours}:${two(minutes)}:${two(seconds)}` : `${minutes}:${two(seconds)}`;
}

/** Formatter names usable in a catalogue value, e.g. "{{amount, moneyCents}}". */
export const FORMATTER_NAMES = [
  "moneyCents",
  "integer",
  "decimal",
  "percent",
  "bytes",
  "date",
  "dateTime",
  "duration",
  "clock",
] as const;
export type FormatterName = (typeof FORMATTER_NAMES)[number];

/**
 * The formatter services already carrying our names, not the instances that own
 * them: `init()` builds a fresh formatter service every time it runs, so an
 * instance guard would skip the second registration and leave every
 * "{{value, name}}" in a catalogue printing its raw JavaScript number. The SPA
 * initialises once, but a test process re-inits the client singleton per file.
 */
const registered = new WeakSet<object>();

/** The zone out of the option bag i18next hands a formatter, which is the whole t() options object. */
function dateOptions(options: unknown): DateFormatOptions {
  const timeZone = (options as { timeZone?: unknown } | undefined)?.timeZone;
  return typeof timeZone === "string" ? { timeZone } : {};
}

/**
 * Registers every formatter on one i18next instance's formatter service, once.
 * Names are our own, so i18next's built-in number/currency/datetime never
 * compete with them.
 *
 * `add`, never `addCached`: i18next's cached formatter keys its cache on the
 * language plus JSON.stringify of the whole t() options bag, and that bag
 * carries the interpolation values, so every distinct amount, date, and
 * duration would retain one more closure for the lifetime of the instance.
 * The server's instance is a process-lifetime singleton and the middleware
 * hands its t() to every request, so that cache would only ever grow. The
 * Intl objects, which are the expensive part, are memoized above by locale
 * and zone, which is bounded.
 */
export function registerFormatters(instance: i18n): void {
  const formatter = instance.services.formatter;
  if (!formatter) throw new Error("i18n: formatter service missing, call after init()");
  if (registered.has(formatter)) return;
  formatter.add("moneyCents", (value, lng) => formatMoneyCents(value, lng));
  formatter.add("integer", (value, lng) => formatInteger(value, lng));
  formatter.add("decimal", (value, lng) => formatDecimal(value, lng));
  formatter.add("percent", (value, lng) => formatPercent(value, lng));
  formatter.add("bytes", (value, lng) => formatBytes(value, lng));
  formatter.add("date", (value, lng, options) => formatDate(value, lng, dateOptions(options)));
  formatter.add("dateTime", (value, lng, options) =>
    formatDateTime(value, lng, dateOptions(options)),
  );
  formatter.add("duration", (value, lng) => formatDuration(value, lng));
  formatter.add("clock", (value) => formatClock(value));
  registered.add(formatter);
}
